import { useMemo, type FC } from 'react'
import { useRelayClient } from '../../../../hooks/index.js'
import type { TxHashes } from '../TransactionModalRenderer.js'
import {
  Flex,
  Anchor,
  Text,
  Skeleton,
  Pill
} from '../../../primitives/index.js'
import { getTxBlockExplorerUrl } from '../../../../utils/getTxBlockExplorerUrl.js'
import { truncateAddress } from '../../../../utils/truncate.js'
import { faRotateRight } from '@fortawesome/free-solid-svg-icons/faRotateRight'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { RelayChain } from '@relayprotocol/relay-sdk'
import type { useRequests } from '@relayprotocol/relay-kit-hooks'

type TransactionsByChainProps = {
  allTxHashes: TxHashes
  fromChain?: RelayChain | null
  toChain?: RelayChain | null
  refundData?: NonNullable<
    ReturnType<typeof useRequests>['data']['0']['data']
  >['refundCurrencyData']
  isSolverStatusTimeout?: boolean
  fillTx?: { txHash: string; chainId: number } | null
}

export const TransactionsByChain: FC<TransactionsByChainProps> = ({
  allTxHashes,
  fromChain,
  toChain,
  refundData,
  isSolverStatusTimeout,
  fillTx
}) => {
  const relayClient = useRelayClient()
  const refundChain = refundData
    ? relayClient?.chains.find(
        (chain) => chain.id === refundData?.currency?.chainId
      )
    : null
  const txHashesByChain = useMemo(() => {
    return allTxHashes.reduce(
      (byChains, txHash) => {
        if (!byChains[txHash.chainId]) {
          byChains[txHash.chainId] = []
        }
        byChains[txHash.chainId].push(txHash)
        return byChains
      },
      {} as Record<number, TxHashes>
    )
  }, [allTxHashes, relayClient?.chains])

  const isSameChain = fromChain?.id === toChain?.id

  const transactions = []

  // 1. Send transaction (always first)
  const sendTxs = fromChain?.id
    ? txHashesByChain[fromChain.id]?.filter((tx) => !tx.isBatchTx).slice(0, 1)
    : []
  if (sendTxs && sendTxs.length > 0 && fromChain) {
    transactions.push({
      label: 'Send tx',
      txHashes: sendTxs,
      isRefund: false
    })
  }

  // 2. Receive transaction (refund or regular)
  if (refundData && refundChain) {
    const refundTxs =
      txHashesByChain[refundChain.id]?.filter((tx) => !tx.isBatchTx) || []
    const refundTxHash = fillTx?.txHash || refundTxs[0]?.txHash
    const refundTxUrl = refundTxHash
      ? getTxBlockExplorerUrl(refundChain.id, relayClient?.chains, refundTxHash)
      : null

    if (refundTxHash && refundTxUrl) {
      transactions.push({
        label: 'Receive tx',
        txHashes: [],
        isRefund: true,
        refundTxHash,
        refundTxUrl
      })
    }
  } else if (toChain?.id && txHashesByChain[toChain.id] && !isSameChain) {
    const receiveTxs = txHashesByChain[toChain.id].filter((tx) => !tx.isBatchTx)
    if (receiveTxs.length > 0) {
      transactions.push({
        label: 'Receive tx',
        txHashes: receiveTxs,
        isRefund: false
      })
    }
  }

  // Return single container with all transactions
  return (
    <Flex direction="column" className="relay:gap-3">
      {transactions.map((transaction, idx) => (
        <Flex justify="between" className="relay:items-center" key={idx}>
          <Flex className="relay:items-center relay:gap-2">
            <Text style="subtitle2" color="subtle">
              {transaction.label}
            </Text>
            {transaction.isRefund ? (
              <Pill
                color="gray"
                className="relay:py-1 relay:px-[6px] relay:flex relay:items-center"
              >
                <FontAwesomeIcon
                  icon={faRotateRight}
                  className="relay:w-[16px] relay:h-[16px] relay:mr-[4px]"
                  color="#889096"
                />{' '}
                <Text style="subtitle3">Refunded</Text>
              </Pill>
            ) : null}
          </Flex>

          {/* Show regular transaction hashes */}
          {!transaction.isRefund && transaction.txHashes.length > 0 ? (
            <Flex direction="column">
              {transaction.txHashes.map(({ txHash, chainId, isBatchTx }) => {
                const txUrl = getTxBlockExplorerUrl(
                  chainId,
                  relayClient?.chains,
                  txHash
                )
                return txUrl && !isBatchTx ? (
                  <Anchor
                    key={txHash}
                    href={txUrl}
                    target="_blank"
                    data-testid="transaction-link"
                  >
                    {truncateAddress(txHash)}
                  </Anchor>
                ) : null
              })}
            </Flex>
          ) : null}

          {/* Show refund transaction hash */}
          {transaction.isRefund &&
          transaction.refundTxHash &&
          transaction.refundTxUrl ? (
            <Flex direction="column">
              <Anchor
                href={transaction.refundTxUrl}
                target="_blank"
                data-testid="refund-transaction-link"
              >
                {truncateAddress(transaction.refundTxHash)}
              </Anchor>
            </Flex>
          ) : null}

          {/* Show pending state */}
          {!transaction.isRefund &&
          transaction.txHashes.length === 0 &&
          !isSolverStatusTimeout ? (
            <Flex direction="column">
              <Text color="red" style="subtitle2">
                Order has not been filled
              </Text>
            </Flex>
          ) : null}

          {/* Show loading state */}
          {!transaction.isRefund &&
          transaction.txHashes.length === 0 &&
          isSolverStatusTimeout ? (
            <Flex direction="column">
              <Skeleton className="relay:h-[20px]" />
            </Flex>
          ) : null}
        </Flex>
      ))}
    </Flex>
  )
}
